import React, { type ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import { formatDistanceToNow } from 'date-fns';
import * as styles from './displays-tab.styles';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import { Center } from '../../../../core/components/center/center.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import StyleConstants from '../../../../core/styles.constants';
import PrimaryButton from '../../../../core/components/buttons/primary-button.component';
import { useKeycloak } from '../../../authentication/keycloak/KeycloakProvider';
import { useOfficeManagerOrAdminOffices } from '../../../offices/hooks/offices.hook';
import { useDashboardOfficeDetail, useDisplays } from '../hooks/dashboard.hook';
import { createDisplay, deleteDisplay, pairDisplay, revokeDisplay, updateDisplay } from '../services/dashboard.service';
import ConfirmationModal from '../../../offices/modals/confirmation-modal.component';
import notify from '../../../../core/components/notify/notify.component';
import {
  type CreateDisplayRequest,
  type Display,
  type DisplayMode,
  type DisplayViewKey,
  type UpdateDisplayRequest,
} from '../models/dashboard.models';
import { usePagedRows, useSortableRows } from '../components/table-controls.hook';
import SortableHeaderCell from '../components/sortable-header-cell.component';
import TablePagination from '../components/table-pagination.component';
import RouteConstants from '../../../../core/routes.constants';

const DISPLAYS_ROWS_PER_PAGE = 8;

// Must stay in sync with SUPPORTED_SCREEN_LANGUAGES in the display screen — an unrecognised ?lang=
// there silently falls back to Dutch, so a link offered here for a language the screen doesn't
// support would quietly hand out a Dutch screen instead.
const SCREEN_LINK_LANGUAGES = ['nl', 'en'];

interface Props {
  readonly selectedOfficeId?: string;
}

const ALL_VIEW_KEYS: DisplayViewKey[] = ['officeTotal', 'rooms', 'parking', 'lunchCount'];

const DEFAULT_ROTATION_INTERVAL_SECONDS = 30;
const DEFAULT_IDLE_TIMEOUT_SECONDS = 60;

// Views the selected office cannot fill with data, mapped to the translation key explaining why.
// An office with no parkings, or with catering switched off, would render an empty parking or lunch
// view on the screen — so those options are not offerable when configuring a display for it.
type UnavailableViews = Partial<Record<DisplayViewKey, string>>;

// The office-detail response is the same payload the display's own screen renders, so it is the
// authoritative answer to "does this office have parkings / is catering on" — no second source to
// drift out of sync with what the kiosk would actually show.
const useUnavailableViews = (officeId?: string): UnavailableViews => {
  const { initialized, detail } = useDashboardOfficeDetail(officeId);

  return useMemo(() => {
    // While the detail is still loading (or failed), assume everything is available: wrongly
    // disabling an option the office does have would be worse than briefly offering one it doesn't.
    if (!initialized || !detail) {
      return {};
    }

    const unavailable: UnavailableViews = {};
    if (detail.parkings.length === 0) {
      unavailable.parking = 'dashboard-display-view-unavailable-parking';
    }
    if (!detail.lunchEnabled) {
      unavailable.lunchCount = 'dashboard-display-view-unavailable-lunch';
    }

    return unavailable;
  }, [initialized, detail]);
};

// An unavailable view is fully disabled, so it can no longer be unticked by hand. Drop it from the
// working selection instead: without this, a display configured before its office lost its parkings
// (or had catering switched off) would be stuck with a greyed-out, unremovable view. The change is
// only persisted when the admin saves, so nothing is rewritten behind their back.
const useDropUnavailableViews = (
  unavailableViews: UnavailableViews,
  setViews: React.Dispatch<React.SetStateAction<DisplayViewKey[]>>,
): void => {
  useEffect(() => {
    setViews((previous) => {
      const supported = previous.filter((view) => !unavailableViews[view]);
      return supported.length === previous.length ? previous : supported;
    });
  }, [unavailableViews, setViews]);
};

const ViewsCheckboxGroup = ({
  idPrefix,
  selectedViews,
  unavailableViews,
  onChange,
}: {
  readonly idPrefix: string;
  readonly selectedViews: DisplayViewKey[];
  readonly unavailableViews: UnavailableViews;
  readonly onChange: (views: DisplayViewKey[]) => void;
}): React.JSX.Element => {
  const { t } = useTranslation();

  const toggleView = (view: DisplayViewKey): void => {
    if (selectedViews.includes(view)) {
      onChange(selectedViews.filter((existing) => existing !== view));
    } else {
      onChange([...selectedViews, view]);
    }
  };

  return (
    <div className={style(styles.checkboxGroup)}>
      {ALL_VIEW_KEYS.map((view) => {
        const unavailableReasonKey = unavailableViews[view];
        const isUnavailable = Boolean(unavailableReasonKey);

        return (
          <label
            key={view}
            className={style(styles.checkboxRow, isUnavailable ? styles.checkboxRowDisabled : undefined)}
            htmlFor={`${idPrefix}-${view}`}
          >
            <input
              id={`${idPrefix}-${view}`}
              type="checkbox"
              checked={selectedViews.includes(view)}
              disabled={isUnavailable}
              onChange={(): void => toggleView(view)}
            />
            {t(`dashboard-display-view-${view}`)}
            {unavailableReasonKey && (
              <span className={style(styles.checkboxHint)}>{t(unavailableReasonKey)}</span>
            )}
          </label>
        );
      })}
    </div>
  );
};

const ShowDisplayNameCheckbox = ({
  id,
  checked,
  onChange,
}: {
  readonly id: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className={style(styles.formField)}>
      <span className={style(styles.fieldLabel)}>{t('dashboard-display-screen-title-label')}</span>
      <label className={style(styles.checkboxRow)} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => onChange(event.target.checked)}
        />
        {t('dashboard-display-show-name-label')}
      </label>
    </div>
  );
};

interface CreateDisplayFormProps {
  readonly officeId: string;
  readonly unavailableViews: UnavailableViews;
  readonly onCreated: () => void;
  readonly onCancel: () => void;
}

const CreateDisplayForm = ({ officeId, unavailableViews, onCreated, onCancel }: CreateDisplayFormProps): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<DisplayMode>(0);
  const [views, setViews] = useState<DisplayViewKey[]>(['officeTotal']);
  const [defaultView, setDefaultView] = useState<DisplayViewKey>('officeTotal');
  const [rotationIntervalSeconds, setRotationIntervalSeconds] = useState(DEFAULT_ROTATION_INTERVAL_SECONDS);
  const [idleTimeoutSeconds, setIdleTimeoutSeconds] = useState(DEFAULT_IDLE_TIMEOUT_SECONDS);
  const [showDisplayName, setShowDisplayName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!views.includes(defaultView) && views.length > 0) {
      setDefaultView(views[0]);
    }
  }, [views, defaultView]);

  useDropUnavailableViews(unavailableViews, setViews);

  const handleCreate = async (): Promise<void> => {
    if (!name.trim()) {
      setErrorKey('dashboard-display-name-required');
      return;
    }

    if (views.length === 0) {
      setErrorKey('dashboard-display-views-required');
      return;
    }

    setErrorKey(undefined);
    setIsSaving(true);

    const request: CreateDisplayRequest = {
      name: name.trim(),
      officeId,
      mode,
      views,
      defaultView: mode === 1 ? defaultView : undefined,
      rotationIntervalSeconds: mode === 0 ? rotationIntervalSeconds : undefined,
      idleTimeoutSeconds: mode === 1 ? idleTimeoutSeconds : undefined,
      showDisplayName,
    };

    const created = await createDisplay(request, keycloak);
    setIsSaving(false);

    if (!created) {
      setErrorKey('dashboard-display-create-error');
      return;
    }

    notify(t('dashboard-display-create-success-message'), true);
    onCreated();
  };

  return (
    <div className={style(styles.sectionCard)}>
      <div className={style(styles.sectionTitle)}>{t('dashboard-display-create-button')}</div>

      <div className={style(styles.formRow)}>
        <div className={style(styles.formField)}>
          <span className={style(styles.fieldLabel)}>{t('dashboard-display-name-label')}</span>
          <input
            className={style(styles.textInput)}
            type="text"
            value={name}
            placeholder={t('dashboard-display-name-placeholder').toString()}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => setName(event.target.value)}
          />
        </div>

        <div className={style(styles.formField)}>
          <span className={style(styles.fieldLabel)}>{t('dashboard-display-mode-label')}</span>
          <div className={style(styles.segmentedControl)}>
            <button type="button" className={style(styles.segmentedOption(mode === 0))} onClick={(): void => setMode(0)}>
              {t('dashboard-display-mode-auto-rotate')}
            </button>
            <button type="button" className={style(styles.segmentedOption(mode === 1))} onClick={(): void => setMode(1)}>
              {t('dashboard-display-mode-touch-select')}
            </button>
          </div>
        </div>

        <ShowDisplayNameCheckbox id="create-display-show-name" checked={showDisplayName} onChange={setShowDisplayName} />
      </div>

      <div className={style(styles.formRow)}>
        <div className={style(styles.formField)}>
          <span className={style(styles.fieldLabel)}>{t('dashboard-display-views-label')}</span>
          <ViewsCheckboxGroup
            idPrefix="create-display-view"
            selectedViews={views}
            unavailableViews={unavailableViews}
            onChange={setViews}
          />
        </div>

        {mode === 0 && (
          <div className={style(styles.formField)}>
            <span className={style(styles.fieldLabel)}>{t('dashboard-display-rotation-interval-label')}</span>
            <input
              className={style(styles.numberInput)}
              type="number"
              min={5}
              max={300}
              value={rotationIntervalSeconds}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => setRotationIntervalSeconds(Number(event.target.value))}
            />
          </div>
        )}

        {mode === 1 && (
          <>
            <div className={style(styles.formField)}>
              <span className={style(styles.fieldLabel)}>{t('dashboard-display-default-view-label')}</span>
              <select
                className={style(styles.selectInput)}
                value={defaultView}
                onChange={(event: ChangeEvent<HTMLSelectElement>): void => setDefaultView(event.target.value as DisplayViewKey)}
              >
                {ALL_VIEW_KEYS.filter((view) => views.includes(view)).map((view) => (
                  <option key={view} value={view}>
                    {t(`dashboard-display-view-${view}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={style(styles.formField)}>
              <span className={style(styles.fieldLabel)}>{t('dashboard-display-idle-timeout-label')}</span>
              <input
                className={style(styles.numberInput)}
                type="number"
                min={10}
                max={600}
                value={idleTimeoutSeconds}
                onChange={(event: ChangeEvent<HTMLInputElement>): void => setIdleTimeoutSeconds(Number(event.target.value))}
              />
            </div>
          </>
        )}
      </div>

      {errorKey && <div className={style(styles.errorText)}>{t(errorKey)}</div>}

      <div className={style(styles.buttonRow)}>
        <div className={style(styles.buttonWrap)}>
          <PrimaryButton loading={isSaving} fullWidth onClick={(): void => { handleCreate(); }}>
            {t('dashboard-display-create-button')}
          </PrimaryButton>
        </div>
        <div className={style(styles.buttonWrap)}>
          <PrimaryButton fullWidth disabled={isSaving} onClick={onCancel}>
            {t('cancel')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

interface BehaviorPanelProps {
  readonly display: Display;
  readonly unavailableViews: UnavailableViews;
  readonly onSaved: () => void;
}

const BehaviorPanel = ({ display, unavailableViews, onSaved }: BehaviorPanelProps): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [mode, setMode] = useState<DisplayMode>(display.mode);
  const [views, setViews] = useState<DisplayViewKey[]>(display.views);
  const [defaultView, setDefaultView] = useState<DisplayViewKey>((display.defaultView as DisplayViewKey) ?? ALL_VIEW_KEYS[0]);
  const [rotationIntervalSeconds, setRotationIntervalSeconds] = useState(
    display.rotationIntervalSeconds || DEFAULT_ROTATION_INTERVAL_SECONDS,
  );
  const [idleTimeoutSeconds, setIdleTimeoutSeconds] = useState(display.idleTimeoutSeconds || DEFAULT_IDLE_TIMEOUT_SECONDS);
  const [showDisplayName, setShowDisplayName] = useState(display.showDisplayName);
  const [isSaving, setIsSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  // The default view only makes sense as one of the currently-checked views — unchecking the
  // view it pointed at (or switching away from touch-select and back) used to leave a stale
  // value that the backend would reject with a 400 on save. Keep it in sync client-side instead
  // of only rejecting it after the fact.
  useEffect(() => {
    if (!views.includes(defaultView) && views.length > 0) {
      setDefaultView(views[0]);
    }
  }, [views, defaultView]);

  useDropUnavailableViews(unavailableViews, setViews);

  const handleSave = async (): Promise<void> => {
    if (views.length === 0) {
      setErrorKey('dashboard-display-views-required');
      return;
    }

    setErrorKey(undefined);
    setSaved(false);
    setIsSaving(true);

    const request: UpdateDisplayRequest = {
      mode,
      views,
      defaultView: mode === 1 ? defaultView : undefined,
      rotationIntervalSeconds: mode === 0 ? rotationIntervalSeconds : undefined,
      idleTimeoutSeconds: mode === 1 ? idleTimeoutSeconds : undefined,
      showDisplayName,
    };

    const updated = await updateDisplay(display.id, request, keycloak);
    setIsSaving(false);

    if (!updated) {
      setErrorKey('dashboard-display-create-error');
      return;
    }

    setSaved(true);
    onSaved();
  };

  return (
    <div className={style(styles.behaviorPanel)}>
      <div className={style(styles.formRow)}>
        <div className={style(styles.formField)}>
          <span className={style(styles.fieldLabel)}>{t('dashboard-display-mode-label')}</span>
          <div className={style(styles.segmentedControl)}>
            <button type="button" className={style(styles.segmentedOption(mode === 0))} onClick={(): void => setMode(0)}>
              {t('dashboard-display-mode-auto-rotate')}
            </button>
            <button type="button" className={style(styles.segmentedOption(mode === 1))} onClick={(): void => setMode(1)}>
              {t('dashboard-display-mode-touch-select')}
            </button>
          </div>
        </div>

        <ShowDisplayNameCheckbox
          id={`behavior-${display.id}-show-name`}
          checked={showDisplayName}
          onChange={setShowDisplayName}
        />
      </div>

      <div className={style(styles.formRow)}>
        <div className={style(styles.formField)}>
          <span className={style(styles.fieldLabel)}>{t('dashboard-display-views-label')}</span>
          <ViewsCheckboxGroup
            idPrefix={`behavior-${display.id}-view`}
            selectedViews={views}
            unavailableViews={unavailableViews}
            onChange={setViews}
          />
        </div>

        {mode === 0 && (
          <div className={style(styles.formField)}>
            <span className={style(styles.fieldLabel)}>{t('dashboard-display-rotation-interval-label')}</span>
            <input
              className={style(styles.numberInput)}
              type="number"
              min={5}
              max={300}
              value={rotationIntervalSeconds}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => setRotationIntervalSeconds(Number(event.target.value))}
            />
          </div>
        )}

        {mode === 1 && (
          <>
            <div className={style(styles.formField)}>
              <span className={style(styles.fieldLabel)}>{t('dashboard-display-default-view-label')}</span>
              <select
                className={style(styles.selectInput)}
                value={defaultView}
                onChange={(event: ChangeEvent<HTMLSelectElement>): void => setDefaultView(event.target.value as DisplayViewKey)}
              >
                {ALL_VIEW_KEYS.filter((view) => views.includes(view)).map((view) => (
                  <option key={view} value={view}>
                    {t(`dashboard-display-view-${view}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={style(styles.formField)}>
              <span className={style(styles.fieldLabel)}>{t('dashboard-display-idle-timeout-label')}</span>
              <input
                className={style(styles.numberInput)}
                type="number"
                min={10}
                max={600}
                value={idleTimeoutSeconds}
                onChange={(event: ChangeEvent<HTMLInputElement>): void => setIdleTimeoutSeconds(Number(event.target.value))}
              />
            </div>
          </>
        )}
      </div>

      {errorKey && <div className={style(styles.errorText)}>{t(errorKey)}</div>}
      {saved && !errorKey && <div className={style(styles.savedText)}>{t('dashboard-display-saved-message')}</div>}

      <div className={style(styles.buttonRow)}>
        <div className={style(styles.buttonWrap)}>
          <PrimaryButton loading={isSaving} fullWidth onClick={(): void => { handleSave(); }}>
            {t('dashboard-display-save-button')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

interface DisplayRowProps {
  readonly display: Display;
  readonly unavailableViews: UnavailableViews;
  readonly onChanged: () => void;
}

const DisplayRow = ({ display, unavailableViews, onChanged }: DisplayRowProps): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [pairingCode, setPairingCode] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  const [pairingErrorKey, setPairingErrorKey] = useState<string | undefined>(undefined);
  const [isBusy, setIsBusy] = useState(false);
  const [showBehaviorPanel, setShowBehaviorPanel] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPublicConfirm, setShowPublicConfirm] = useState(false);
  const [copiedLanguage, setCopiedLanguage] = useState<string | undefined>(undefined);

  const viewsSummary = display.views.map((view) => t(`dashboard-display-view-${view}`)).join(', ');
  const lastSeenText = display.lastSeenAt
    ? formatDistanceToNow(new Date(display.lastSeenAt), { addSuffix: true })
    : t('dashboard-display-last-seen-never');

  // The screen reads ?lang= from the URL, so one display can be handed out as either a Dutch or an
  // English link without needing a second display row for the same office.
  const screenUrlFor = (language: string): string =>
    `${window.location.origin}/${RouteConstants.PATH_DISPLAY_SCREEN}/${display.id}?lang=${language}`;

  const copyScreenUrl = (language: string): void => {
    navigator.clipboard.writeText(screenUrlFor(language)).then(() => {
      setCopiedLanguage(language);
      setTimeout(() => setCopiedLanguage(undefined), 2000);
    });
  };

  const handlePair = async (): Promise<void> => {
    if (!pairingCode.trim()) {
      return;
    }

    setPairingErrorKey(undefined);
    setIsPairing(true);
    const result = await pairDisplay(pairingCode.trim(), display.id, keycloak);
    setIsPairing(false);

    // Success returns the updated Display itself (status becomes 'live'); the backend doesn't
    // distinguish invalid vs. expired vs. revoked codes in the error body, so a single message
    // covers all failure cases here.
    if (!result) {
      setPairingErrorKey('dashboard-display-pairing-error');
      return;
    }

    setPairingCode('');
    notify(t('dashboard-display-pairing-success-message'), true);
    onChanged();
  };

  const handleTogglePause = async (): Promise<void> => {
    const willPause = display.status !== 'paused';
    setIsBusy(true);
    const updated = await updateDisplay(display.id, { isPaused: willPause }, keycloak);
    setIsBusy(false);

    if (updated) {
      notify(t(willPause ? 'dashboard-display-pause-success-message' : 'dashboard-display-resume-success-message'), true);
    } else {
      notify(t(willPause ? 'dashboard-display-pause-error-message' : 'dashboard-display-resume-error-message'), false);
    }
    onChanged();
  };

  const handleRevoke = async (): Promise<void> => {
    setShowRevokeConfirm(false);
    setIsBusy(true);
    const success = await revokeDisplay(display.id, keycloak);
    setIsBusy(false);

    notify(t(success ? 'dashboard-display-revoke-success-message' : 'dashboard-display-revoke-error-message'), success);
    onChanged();
  };

  const handleDelete = async (): Promise<void> => {
    setShowDeleteConfirm(false);
    setIsBusy(true);
    const success = await deleteDisplay(display.id, keycloak);
    setIsBusy(false);

    notify(t(success ? 'dashboard-display-delete-success-message' : 'dashboard-display-delete-error-message'), success);
    onChanged();
  };

  const setPublic = async (isPublic: boolean): Promise<void> => {
    setShowPublicConfirm(false);
    setIsBusy(true);
    const updated = await updateDisplay(display.id, { isPublic }, keycloak);
    setIsBusy(false);

    if (updated) {
      notify(t(isPublic ? 'dashboard-display-public-on-success-message' : 'dashboard-display-public-off-success-message'), true);
    } else {
      notify(t('dashboard-display-public-error-message'), false);
    }
    onChanged();
  };

  // A revoked display serves nothing to anyone, so sharing it is meaningless until it's re-paired.
  const isPublicToggleDisabled = isBusy || display.status === 'revoked';

  // Turning sharing ON exposes this office's occupancy to anyone with the link, so it asks first.
  // Turning it OFF only ever reduces access — no need to interrupt someone revoking exposure.
  const handleTogglePublic = (): void => {
    if (display.isPublic) {
      setPublic(false);
      return;
    }

    setShowPublicConfirm(true);
  };

  return (
    <>
      <tr>
        <td className={style(styles.td)}>{display.name}</td>
        <td className={style(styles.td)}>
          {display.mode === 0 ? t('dashboard-display-mode-auto-rotate') : t('dashboard-display-mode-touch-select')}
        </td>
        <td className={style(styles.td)}>{viewsSummary}</td>
        <td className={style(styles.td)}>
          <span className={style(styles.statusBadge(display.status))}>{t(`dashboard-display-status-${display.status}`)}</span>
        </td>
        <td className={style(styles.td)}>{lastSeenText}</td>
        <td className={style(styles.td)}>
          <div className={style(styles.linkCopyGroup)}>
            {SCREEN_LINK_LANGUAGES.map((language) => (
              <button
                key={language}
                type="button"
                className={style(styles.linkCopyButton)}
                onClick={(): void => copyScreenUrl(language)}
                title={screenUrlFor(language)}
              >
                {copiedLanguage === language
                  ? t('dashboard-display-link-copied')
                  : `${t('dashboard-display-copy-link')} ${language.toUpperCase()}`}
              </button>
            ))}
          </div>
        </td>
        <td className={style(styles.td)}>
          <div className={style(styles.publicToggleCell)}>
            <button
              type="button"
              role="switch"
              aria-checked={display.isPublic}
              aria-label={t('dashboard-displays-table-public-access').toString()}
              className={style(styles.publicToggleTrack(display.isPublic, isPublicToggleDisabled))}
              disabled={isPublicToggleDisabled}
              onClick={handleTogglePublic}
            >
              <span className={style(styles.publicToggleKnob(display.isPublic))} />
            </button>
            <span className={style(styles.publicToggleLabel(display.isPublic))}>
              {display.isPublic ? t('dashboard-display-public-on-label') : t('dashboard-display-public-off-label')}
            </span>
          </div>
        </td>
        <td className={style(styles.td)}>
          <div className={style(styles.actionsCell)}>
            {/* Shown for any non-revoked display, not just never-paired ones — same as the facility
                displays tab: a replacement or reset screen shows a fresh pairing code at any point
                in a display's life, and without an input here there would be no way to enter it.
                Pairing again re-binds the display to the new screen and invalidates the previous
                screen's token, which then falls back to its own pairing code. */}
            {display.status !== 'revoked' && (
              <>
                <input
                  className={style(styles.pairingInput)}
                  type="text"
                  maxLength={6}
                  placeholder={t('dashboard-display-pairing-code-placeholder').toString()}
                  value={pairingCode}
                  onChange={(event: ChangeEvent<HTMLInputElement>): void => setPairingCode(event.target.value.toUpperCase())}
                />
                <button
                  type="button"
                  className={style(styles.smallButton)}
                  disabled={isPairing || !pairingCode.trim()}
                  onClick={(): void => {
                    handlePair();
                  }}
                >
                  {display.status === 'pending'
                    ? t('dashboard-display-pairing-button')
                    : t('dashboard-display-repair-button')}
                </button>
              </>
            )}

            {(display.status === 'live' || display.status === 'paused') && (
              <button type="button" className={style(styles.smallButton)} disabled={isBusy} onClick={(): void => { handleTogglePause(); }}>
                {display.status === 'paused' ? t('dashboard-display-resume-button') : t('dashboard-display-pause-button')}
              </button>
            )}

            {display.status !== 'revoked' && (
              <button
                type="button"
                className={style(styles.smallButtonDanger)}
                disabled={isBusy}
                onClick={(): void => setShowRevokeConfirm(true)}
              >
                {t('dashboard-display-revoke-button')}
              </button>
            )}

            <button
              type="button"
              className={style(styles.smallButtonDanger)}
              disabled={isBusy}
              onClick={(): void => setShowDeleteConfirm(true)}
            >
              {t('dashboard-display-delete-button')}
            </button>

            <button type="button" className={style(styles.smallButton)} onClick={(): void => setShowBehaviorPanel((prev) => !prev)}>
              {t('dashboard-display-edit-behavior-button')}
            </button>
          </div>
          {pairingErrorKey && <div className={style(styles.errorText)}>{t(pairingErrorKey)}</div>}
        </td>
      </tr>
      {showBehaviorPanel && (
        <tr>
          <td className={style(styles.td)} colSpan={8}>
            <BehaviorPanel display={display} unavailableViews={unavailableViews} onSaved={onChanged} />
          </td>
        </tr>
      )}
      <ConfirmationModal
        showModal={showRevokeConfirm}
        itemName={display.name}
        handleAccept={handleRevoke}
        handleClose={(): void => setShowRevokeConfirm(false)}
      />
      <ConfirmationModal
        showModal={showDeleteConfirm}
        itemName={display.name}
        handleAccept={handleDelete}
        handleClose={(): void => setShowDeleteConfirm(false)}
      />
      <ConfirmationModal
        showModal={showPublicConfirm}
        itemName={display.name}
        messageKey="dashboard-display-public-confirm-message"
        confirmLabelKey="dashboard-display-public-confirm-button"
        handleAccept={(): void => { setPublic(true); }}
        handleClose={(): void => setShowPublicConfirm(false)}
      />
    </>
  );
};

const DisplaysTab = ({ selectedOfficeId }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const { offices } = useOfficeManagerOrAdminOffices();
  const sortedOffices = useMemo(() => [...(offices ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [offices]);
  const [officeId, setOfficeId] = useState<string | undefined>(selectedOfficeId);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { initialized, displays, error, refresh } = useDisplays(officeId);
  const unavailableViews = useUnavailableViews(officeId);

  useEffect(() => {
    if (selectedOfficeId) {
      setOfficeId(selectedOfficeId);
    }
  }, [selectedOfficeId]);

  useEffect(() => {
    if (!officeId && sortedOffices.length > 0 && sortedOffices[0].id) {
      setOfficeId(sortedOffices[0].id);
    }
  }, [sortedOffices, officeId]);

  const onChangeOffice = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
    setOfficeId(event.target.value || undefined);
    setShowCreateForm(false);
  }, []);

  const {
    sortedRows: sortedDisplays,
    sortKey: displaysSortKey,
    sortDirection: displaysSortDirection,
    toggleSort: toggleDisplaysSort,
  } = useSortableRows<Display>(
    displays ?? [],
    (display, key) => {
      switch (key) {
        case 'mode':
          return display.mode;
        case 'status':
          return display.status;
        case 'lastSeen':
          return display.lastSeenAt ? new Date(display.lastSeenAt).getTime() : 0;
        case 'name':
        default:
          return display.name;
      }
    },
    'name',
  );
  const { pagedRows: pagedDisplays, page: displaysPage, setPage: setDisplaysPage, pageCount: displaysPageCount } = usePagedRows(
    sortedDisplays,
    DISPLAYS_ROWS_PER_PAGE,
  );

  return (
    <div>
      <div className={style(styles.headerRow(isMobile))}>
        <div className={style(styles.selectorRow)}>
          <span className={style(styles.selectorLabel)}>{t('dashboard-office-select-placeholder')}</span>
          <select className={style(styles.officeSelect)} value={officeId ?? ''} onChange={onChangeOffice}>
            <option value="">{t('dashboard-office-select-placeholder')}</option>
            {sortedOffices.map((office) => (
              <option key={office.id} value={office.id}>
                {office.name}
              </option>
            ))}
          </select>
        </div>

        {officeId && !showCreateForm && (
          <div className={style(styles.newButtonWrap)}>
            <PrimaryButton fullWidth onClick={(): void => setShowCreateForm(true)}>
              {t('dashboard-displays-new-button')}
            </PrimaryButton>
          </div>
        )}
      </div>

      {!officeId && <div className={style(styles.emptyMessage)}>{t('dashboard-office-select-prompt')}</div>}

      {officeId && showCreateForm && (
        <CreateDisplayForm
          officeId={officeId}
          unavailableViews={unavailableViews}
          onCreated={(): void => {
            setShowCreateForm(false);
            refresh();
          }}
          onCancel={(): void => setShowCreateForm(false)}
        />
      )}

      {officeId && !initialized && (
        <Center>
          <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
        </Center>
      )}

      {officeId && initialized && error && <span className={style(styles.errorText)}>{t('dashboard-displays-error-message')}</span>}

      {officeId && initialized && !error && sortedDisplays.length === 0 && !showCreateForm && (
        <div className={style(styles.emptyStateWrap)}>
          <div className={style(styles.emptyStateTitle)}>{t('dashboard-displays-empty-title')}</div>
          <div className={style(styles.emptyStateDescription)}>{t('dashboard-displays-empty-description')}</div>
          <div className={style(styles.emptyStateButtonWrap)}>
            <PrimaryButton fullWidth onClick={(): void => setShowCreateForm(true)}>
              {t('dashboard-displays-new-button')}
            </PrimaryButton>
          </div>
        </div>
      )}

      {officeId && initialized && !error && sortedDisplays.length > 0 && (
        <div className={style(styles.sectionCard)}>
          <div className={styles.tableWrap}>
            <table className={style(styles.table)}>
              <thead>
                <tr>
                  <SortableHeaderCell
                    className={style(styles.th)}
                    label={t('dashboard-displays-table-name')}
                    sortKeyName="name"
                    activeSortKey={displaysSortKey}
                    sortDirection={displaysSortDirection}
                    onSort={toggleDisplaysSort}
                  />
                  <SortableHeaderCell
                    className={style(styles.th)}
                    label={t('dashboard-displays-table-mode')}
                    sortKeyName="mode"
                    activeSortKey={displaysSortKey}
                    sortDirection={displaysSortDirection}
                    onSort={toggleDisplaysSort}
                  />
                  <th className={style(styles.th)}>{t('dashboard-displays-table-views')}</th>
                  <SortableHeaderCell
                    className={style(styles.th)}
                    label={t('dashboard-displays-table-status')}
                    sortKeyName="status"
                    activeSortKey={displaysSortKey}
                    sortDirection={displaysSortDirection}
                    onSort={toggleDisplaysSort}
                  />
                  <SortableHeaderCell
                    className={style(styles.th)}
                    label={t('dashboard-displays-table-last-seen')}
                    sortKeyName="lastSeen"
                    activeSortKey={displaysSortKey}
                    sortDirection={displaysSortDirection}
                    onSort={toggleDisplaysSort}
                  />
                  <th className={style(styles.th)}>{t('dashboard-displays-table-link')}</th>
                  <th className={style(styles.th)}>{t('dashboard-displays-table-public-access')}</th>
                  <th className={style(styles.th)}>{t('dashboard-displays-table-actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedDisplays.map((display) => (
                  <DisplayRow
                    key={display.id}
                    display={display}
                    unavailableViews={unavailableViews}
                    onChanged={refresh}
                  />
                ))}
              </tbody>
            </table>
            <TablePagination page={displaysPage} pageCount={displaysPageCount} onChangePage={setDisplaysPage} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplaysTab;
